import { useCallback, useEffect, useRef, useState } from "react";

const dragThresholdPx = 8;
const toggleDistancePx = 50;
const interactiveSelector = "a, button, input, textarea, select, [role='button']";

export const useMobileNavDrag = (props: { maxHeight: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHeight, setDragHeight] = useState(0);

  const maxHeightRef = useRef(props.maxHeight);
  const isExpandedRef = useRef(isExpanded);
  const isDraggingRef = useRef(false);
  const isTouchingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const currentHeightRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    maxHeightRef.current = props.maxHeight;
  }, [props.maxHeight]);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const onTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    const target = event.target;
    if (target instanceof Element && target.closest(interactiveSelector)) return;

    isTouchingRef.current = true;
    startYRef.current = touch.clientY;
    startHeightRef.current = isExpandedRef.current ? maxHeightRef.current : 0;
    currentHeightRef.current = startHeightRef.current;
  }, []);

  const onTouchMove = useCallback((event: TouchEvent) => {
    if (!isTouchingRef.current) return;

    const touch = event.touches[0];
    if (!touch) return;

    const delta = startYRef.current - touch.clientY;

    if (!isDraggingRef.current) {
      if (Math.abs(delta) < dragThresholdPx) return;
      isDraggingRef.current = true;
      setIsDragging(true);
      setDragHeight(startHeightRef.current);
    }

    event.preventDefault();
    currentHeightRef.current = Math.min(maxHeightRef.current, Math.max(0, startHeightRef.current + delta));

    if (frameRef.current !== null) return;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      setDragHeight(currentHeightRef.current);
    });
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!isTouchingRef.current) return;
    isTouchingRef.current = false;

    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    setIsDragging(false);

    const delta = currentHeightRef.current - startHeightRef.current;
    if (delta > toggleDistancePx) setIsExpanded(true);
    else if (delta < -toggleDistancePx) setIsExpanded(false);
  }, []);

  useEffect(() => {
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);

    return () => {
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [onTouchMove, onTouchEnd]);

  const height = isDragging ? dragHeight : isExpanded ? props.maxHeight : 0;

  return { isExpanded, setIsExpanded, isDragging, height, onTouchStart };
};
